import { Typography } from '@sureco/design-system'
import { Button } from '@surecompanies/core_components'
import { colorTheme } from '@surecompanies/theme'
import useFeatureFlags from 'hooks/useFeatureFlags'
import React, { memo, useContext } from 'react'
import { LifeEventsContext } from 'routes/LifeEvents/LifeEventsContext'

export type AddBenefitButtonProps = {
  bundle: { BundlePublicKey: string; added: boolean }
  isProcessing?: boolean
  onRemoveFromCart: (
    bundlePublicKey: string,
    qleEnrollmentKey?: string,
    options?: {
      isImpersonated: boolean
      isTestUser: boolean
      isActivityLogEnabled: boolean
    }
  ) => void
  onAddToCart: (
    bundlePublicKey: string,
    qleEnrollmentKey?: string,
    options?: {
      isImpersonated: boolean
      isTestUser: boolean
      isActivityLogEnabled: boolean
    }
  ) => void
  isImpersonated?: boolean
  isTestUser?: boolean
}

const AddBenefitButton = ({
  bundle: { BundlePublicKey, added },
  isProcessing,
  onRemoveFromCart,
  onAddToCart,
  isImpersonated,
  isTestUser,
}: AddBenefitButtonProps) => {
  const qleCtx = useContext(LifeEventsContext)
  const { flags } = useFeatureFlags()
  const isActivityLogEnabled = flags?.['enrollme.activity-log-reason-popup-enabled']

  const options = {
    isImpersonated,
    isTestUser,
    isActivityLogEnabled,
  }

  const onAddToCartHandler = () => {
    if (qleCtx) {
      const qleEnrollmentKey = qleCtx.state.shared.record?.qleResult[0]?.enrollmentPublicKey
      if (!qleEnrollmentKey) {
        throw new Error('QLE add to cart without QLE enrollment key')
      }
      onAddToCart(BundlePublicKey, qleEnrollmentKey, options)
    } else {
      onAddToCart(BundlePublicKey, undefined, options)
    }
  }

  const onRemoveFromCartHandler = () => {
    if (qleCtx) {
      const qleEnrollmentKey = qleCtx.state.shared.record?.qleResult[0]?.enrollmentPublicKey
      if (!qleEnrollmentKey) {
        throw new Error('QLE add to cart without QLE enrollment key')
      }
      onRemoveFromCart(BundlePublicKey, qleEnrollmentKey, options)
    } else {
      onRemoveFromCart(BundlePublicKey, undefined, options)
    }
  }

  return (
    <div style={{ width: '100%' }}>
      {added ? (
        <Button
          size="large"
          loading={isProcessing}
          onClick={onRemoveFromCartHandler}
          style={{ backgroundColor: colorTheme.primaryHover, color: 'white' }}
          block
        >
          <Typography size={14} bold>
            {isProcessing ? 'Removing from Benefits...' : 'Added to My Benefits'}
          </Typography>
        </Button>
      ) : (
        <Button
          type="primary"
          loading={isProcessing}
          size="large"
          onClick={onAddToCartHandler}
          block
          style={{ boxShadow: 'none' }}
        >
          <Typography size={14} bold>
            {isProcessing ? 'Adding to Benefits...' : 'Add to My Benefits'}
          </Typography>
        </Button>
      )}
    </div>
  )
}

export default memo(AddBenefitButton)
